import { compact, keyBy } from 'lodash'
import { decodeEventLog, erc20Abi, erc721Abi, Hex, parseAbiItem } from 'viem'

import { SupportedChainId } from '~/constants/chains'
import { IAddress } from '~/types'
import { Address, EvmAddress } from '~/utils/address'
import { captureException } from '~/utils/errors'
import { unwrapToken } from '~/utils/tokens'

import type {
  AllowanceSnapshot,
  ApprovalChange,
  CombinedStateChangeResult,
  CustomLog,
  NFTStateChange,
  NFTTransferEvent,
  PartitionedLogs,
  StateChange,
  TransferEvent,
} from '~/components/txn-display/state-change/types'

export const ERC20_TRANSFER_TOPIC =
  '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef' as Hex

export const ERC20_APPROVAL_TOPIC =
  '0x8c5be1e5ebec7d5bd14f71427d1e84f3dd0314c0f7b2291e5b200ac8c7c3b925' as Hex

export const ERC1155_TRANSFER_SINGLE_TOPIC =
  '0xc3d58168c5ae7397731d063d5bbf3d657854427343f4c083240f7aacaa2d0f62' as Hex

export const ERC1155_TRANSFER_BATCH_TOPIC =
  '0x4a39dc06d4c0dbc64b70af90fd698a233a518aa5d07e595d983b8c0526c8f7fb' as Hex

// Chains with EIP-7708 (Arc) also log every native transfer as an ERC-20
// style `Transfer` from this system address. The simulation and the trace
// already report the native leg, so reading the log too would show each native
// transfer twice, once as an unknown token.
const EIP_7708_SYSTEM_ADDRESS = '0xfffffffffffffffffffffffffffffffffffffffe'

export const deriveStateChangeResult = ({
  addressesToWatch,
  chainId,
  logs,
  allowances = [],
}: {
  addressesToWatch: IAddress[]
  chainId: SupportedChainId
  logs: CustomLog[]
  allowances?: AllowanceSnapshot[]
}): CombinedStateChangeResult => {
  const watchSet = buildWatchAddressSet(addressesToWatch)
  const {
    erc20Transfers,
    erc721Transfers,
    erc1155SingleTransfers,
    erc1155BatchTransfers,
    approvals,
  } = partitionLogs(logs)

  const transferEvents = compact(erc20Transfers.map(decodeErc20Transfer))
  const watchedTransferEvents = filterEventsForWatchlist(
    transferEvents,
    watchSet,
  )
  const stateChanges = buildStateChangesFromTransfers(
    watchedTransferEvents,
    watchSet,
    chainId,
  )

  const nftEvents = [
    ...compact(erc721Transfers.map(decodeErc721Transfer)),
    ...compact(erc1155SingleTransfers.map(decodeErc1155SingleTransfer)),
    ...erc1155BatchTransfers.flatMap(decodeErc1155BatchTransfer),
  ]
  const watchedNftEvents = filterEventsForWatchlist(nftEvents, watchSet)
  const nftStateChanges = buildNFTStateChanges(
    watchedNftEvents,
    watchSet,
    chainId,
  )

  const approvalChanges = applyAllowances({
    approvalChanges: buildApprovalChanges(approvals, watchSet, chainId),
    allowances,
    stateChanges,
  })

  return {
    stateChanges,
    approvalChanges,
    nftStateChanges,
  }
}

const partitionLogs = (logs: CustomLog[] = []): PartitionedLogs => {
  const erc20Transfers = logs.filter(
    (log) =>
      log.topics[0] === ERC20_TRANSFER_TOPIC &&
      log.topics.length === 3 &&
      !Address.equal(log.address, EIP_7708_SYSTEM_ADDRESS, 'evm'),
  )

  const erc721Transfers = logs.filter(
    (log) => log.topics[0] === ERC20_TRANSFER_TOPIC && log.topics.length === 4,
  )

  const approvals = logs.filter((log) => log.topics[0] === ERC20_APPROVAL_TOPIC)

  const erc1155SingleTransfers = logs.filter(
    (log) => log.topics[0] === ERC1155_TRANSFER_SINGLE_TOPIC,
  )

  const erc1155BatchTransfers = logs.filter(
    (log) => log.topics[0] === ERC1155_TRANSFER_BATCH_TOPIC,
  )

  return {
    erc20Transfers,
    erc721Transfers,
    erc1155SingleTransfers,
    erc1155BatchTransfers,
    approvals,
  }
}

const buildWatchAddressSet = (
  addressesToWatch: IAddress[],
): ReadonlySet<IAddress> => {
  return new Set(addressesToWatch.map((address) => EvmAddress.format(address)))
}

const decodeErc20Transfer = (log: CustomLog): TransferEvent | undefined => {
  try {
    const decodedLog = decodeEventLog({
      abi: erc20Abi,
      data: log.data,
      topics: log.topics,
    })

    if (decodedLog.eventName === 'Transfer') {
      return {
        token: unwrapToken(EvmAddress.format(log.address)),
        from: EvmAddress.format(decodedLog.args.from),
        to: EvmAddress.format(decodedLog.args.to),
        amount: decodedLog.args.value,
      }
    }
  } catch (error) {
    captureException(new Error('Failed to decode transfer event'), {
      extra: { log, error },
    })
  }
}

const decodeErc721Transfer = (log: CustomLog): NFTTransferEvent | undefined => {
  try {
    const decodedLog = decodeEventLog({
      abi: erc721Abi,
      data: log.data,
      topics: log.topics,
    })

    if (decodedLog.eventName === 'Transfer') {
      return {
        token: EvmAddress.format(log.address),
        from: EvmAddress.format(decodedLog.args.from),
        to: EvmAddress.format(decodedLog.args.to),
        tokenId: decodedLog.args.tokenId,
        amount: 1n,
        tokenType: 'ERC721',
      }
    }
  } catch (error) {
    captureException(new Error('Failed to decode ERC-721 transfer event'), {
      extra: { log, error },
    })
    return undefined
  }
}

const decodeErc1155SingleTransfer = (
  log: CustomLog,
): NFTTransferEvent | undefined => {
  try {
    const decodedLog = decodeEventLog({
      abi: [
        parseAbiItem(
          'event TransferSingle(address indexed operator, address indexed from, address indexed to, uint256 id, uint256 value)',
        ),
      ],
      data: log.data,
      topics: log.topics,
    })

    if (decodedLog.eventName === 'TransferSingle') {
      return {
        token: EvmAddress.format(log.address),
        from: EvmAddress.format(decodedLog.args.from),
        to: EvmAddress.format(decodedLog.args.to),
        tokenId: decodedLog.args.id,
        amount: decodedLog.args.value,
        tokenType: 'ERC1155',
      }
    }
  } catch (error) {
    captureException(new Error('Failed to decode ERC-1155 single transfer'), {
      extra: { log, error },
    })
    return undefined
  }
}

const decodeErc1155BatchTransfer = (log: CustomLog): NFTTransferEvent[] => {
  try {
    const decodedLog = decodeEventLog({
      abi: [
        parseAbiItem(
          'event TransferBatch(address indexed operator, address indexed from, address indexed to, uint256[] ids, uint256[] values)',
        ),
      ],
      data: log.data,
      topics: log.topics,
    })

    if (decodedLog.eventName === 'TransferBatch') {
      // Validate that ids and values arrays have matching lengths
      if (decodedLog.args.ids.length !== decodedLog.args.values.length) {
        captureException(
          new Error('ERC-1155 batch transfer array length mismatch'),
          {
            extra: {
              log,
              idsLength: decodedLog.args.ids.length,
              valuesLength: decodedLog.args.values.length,
            },
          },
        )
        return []
      }

      return decodedLog.args.ids.map((id, index) => ({
        token: EvmAddress.format(log.address),
        from: EvmAddress.format(decodedLog.args.from),
        to: EvmAddress.format(decodedLog.args.to),
        tokenId: id,
        amount: decodedLog.args.values[index],
        tokenType: 'ERC1155',
      }))
    }
  } catch (error) {
    captureException(new Error('Failed to decode ERC-1155 batch transfer'), {
      extra: { log, error },
    })
    return []
  }

  return []
}

const filterEventsForWatchlist = <T extends { from: IAddress; to: IAddress }>(
  events: T[],
  watchSet: ReadonlySet<IAddress>,
) =>
  events.filter((event) => watchSet.has(event.from) || watchSet.has(event.to))

const aggregateTransferAmounts = (
  events: TransferEvent[],
  watchSet: ReadonlySet<IAddress>,
) => {
  const eventsPerAddress = events.reduce(
    (acc, event) => {
      if (watchSet.has(event.from)) {
        acc[event.from] = acc[event.from] || []
        acc[event.from].push(event)
      }

      if (watchSet.has(event.to)) {
        acc[event.to] = acc[event.to] || []
        acc[event.to].push(event)
      }

      return acc
    },
    {} as Record<IAddress, TransferEvent[]>,
  )

  return Object.entries(eventsPerAddress).reduce(
    (acc, [address, accountEvents]) => {
      const account = address as IAddress
      accountEvents.forEach((event) => {
        acc[account] = acc[account] || {}
        acc[account][event.token] = acc[account][event.token] || 0n

        if (event.from === event.to) return

        if (event.from === address) {
          acc[account][event.token] -= event.amount
        } else {
          acc[account][event.token] += event.amount
        }
      })
      return acc
    },
    {} as Record<IAddress, Record<IAddress, bigint>>,
  )
}

const buildStateChangesFromTransfers = (
  events: TransferEvent[],
  watchSet: ReadonlySet<IAddress>,
  chainId: SupportedChainId,
) => {
  const transferAmountsPerToken = aggregateTransferAmounts(events, watchSet)

  return Object.entries(transferAmountsPerToken).flatMap(
    ([account, amounts]) => {
      const accountAddress = account as IAddress
      return Object.entries(amounts).map(([token, amount]) => ({
        address: accountAddress,
        token: token as IAddress,
        chainId,
        amount,
      }))
    },
  )
}

const buildNFTStateChanges = (
  events: NFTTransferEvent[],
  watchSet: ReadonlySet<IAddress>,
  chainId: SupportedChainId,
) =>
  events.flatMap((event) => {
    const changes: NFTStateChange[] = []
    const from = EvmAddress.format(event.from)
    const to = EvmAddress.format(event.to)

    if (watchSet.has(from)) {
      changes.push({
        address: from,
        token: event.token,
        chainId,
        tokenId: event.tokenId,
        amount: event.amount,
        tokenType: event.tokenType,
        direction: 'outflow',
      })
    }

    if (watchSet.has(to)) {
      changes.push({
        address: to,
        token: event.token,
        chainId,
        tokenId: event.tokenId,
        amount: event.amount,
        tokenType: event.tokenType,
        direction: 'inflow',
      })
    }

    return changes
  })

export const decodeApprovalLog = (
  log: CustomLog,
): AllowanceSnapshot | undefined => {
  try {
    const decodedLog = decodeEventLog({
      abi: erc20Abi,
      data: log.data,
      topics: log.topics,
    })

    if (decodedLog.eventName === 'Approval') {
      return {
        token: EvmAddress.format(log.address),
        owner: EvmAddress.format(decodedLog.args.owner),
        spender: EvmAddress.format(decodedLog.args.spender),
        amount: decodedLog.args.value,
      }
    }
  } catch {
    return undefined
  }
}

const RESIDUAL_ALLOWANCE_DIVISOR = 10n

export const getAllowanceKey = (allowance: {
  owner: string
  token: string
  spender: string
}) => `${allowance.owner}-${allowance.token}-${allowance.spender}`

// Tokens like USDC and WETH9 spend allowance without an Approval log, so the
// log amount can overstate what remains. A post-state read for the same
// owner, token and spender replaces it, but the read may only lower the
// amount by what actually left the owner: a spender that moves tokens out and
// back (or to the owner itself) consumes allowance without a net outflow, and
// only during a simulation it can detect, so that read must not hide the
// approval. An approval whose spender used all but 10% of it, like an
// exact-out swap's slippage buffer, is treated as consumed.
export const applyAllowances = ({
  approvalChanges,
  allowances,
  stateChanges,
}: {
  approvalChanges: ApprovalChange[]
  allowances: AllowanceSnapshot[]
  stateChanges: StateChange[]
}): ApprovalChange[] => {
  const allowanceByKey = keyBy(allowances, getAllowanceKey)
  const netChangeOf = (approval: ApprovalChange) =>
    stateChanges.find(
      (change) =>
        change.address === approval.owner &&
        change.token === unwrapToken(approval.token),
    )?.amount ?? 0n

  return approvalChanges.map((approval) => {
    const read = allowanceByKey[getAllowanceKey(approval)]?.amount
    if (read === undefined) return approval

    const netOutflow = -netChangeOf(approval)
    const floor = approval.amount - (netOutflow > 0n ? netOutflow : 0n)

    const remaining = read > floor ? read : floor
    const spent = approval.amount - remaining
    const isConsumed =
      spent > 0n && remaining * RESIDUAL_ALLOWANCE_DIVISOR <= spent

    return { ...approval, amount: isConsumed ? 0n : remaining }
  })
}

const buildApprovalChanges = (
  logs: CustomLog[],
  watchSet: ReadonlySet<IAddress>,
  chainId: SupportedChainId,
): ApprovalChange[] => {
  const ownedApprovals = compact(logs.map(decodeApprovalLog)).filter((event) =>
    watchSet.has(event.owner),
  )

  // An Approval log carries the new absolute allowance, so the last one per
  // owner, token and spender (keyBy keeps the last entry per key) is the
  // latest allowance set.
  const finalApprovals = Object.values(keyBy(ownedApprovals, getAllowanceKey))

  return finalApprovals.map((approval) => ({ ...approval, chainId }))
}
